import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import TopNavBar from "../components/TopNavBar";
import { useAuth } from "../context/AuthContext";
import {
  getRoomRestrictionTypeFromLabel,
  getRoomRestrictionColor,
  stripRoomRestrictionSuffix,
} from "../utils/roomRestriction";
import {
  assignDeviceOperation,
  getDeviceSchedules,
  getLatestDeviceOperations,
  notifyRcTekniker,
} from "../api/deviceOperations";
import { getCleaningPerformanceRows } from "../api/performance";
import { getAllUsers } from "../api/users";
import useSSESubscription from "../hooks/useSSESubscription";
import auhMap from "../assets/images/AUH-basement-black-white-large.png";

const ALERT_STYLES = {
  red: {
    pin: "#e53935",
    typeBg: "#fee2e2b2",
    typeText: "#000000",
  },
  yellow: {
    pin: "#f5ca0b",
    typeBg: "#fef3c794",
    typeText: "#000000",
  },
  green: {
    pin: "#23be14",
    typeBg: "#dcfce7b2",
    typeText: "#000000",
  },
  actioned: {
    pin: "#2563eb",
  },
};

const DEFAULT_PIN_COLOR = "#23be14";

const PIN_LEGEND_ITEMS = [
  { color: ALERT_STYLES.red.pin, label: "Kræver handling" },
  { color: ALERT_STYLES.yellow.pin, label: "Til information" },
  { color: ALERT_STYLES.actioned.pin, label: "Tildelt" },
  { color: DEFAULT_PIN_COLOR, label: "OK" },
];

function getPercentageColour(realizedPctNumber) {
  if (realizedPctNumber >= 85) return "green";
  if (realizedPctNumber >= 75) return "orange";
  return "#123f52";
}

// room_type_name may carry a "(male)"/"(female)" suffix from the automation
// service's room_restriction append; colour it so it stands out at a glance.
function getRoomRestrictionColorFromLabel(roomTypeName) {
  const type = getRoomRestrictionTypeFromLabel(roomTypeName);
  return type ? getRoomRestrictionColor(type) : undefined;
}

const FLOOR_LEVELS = [{ id: "kaelder", label: "Kælder" }];

const REASON_LABEL_BY_CODE = {
  NOT_RETURNED_TO_CHARGER: "Ikke retur til lader",
  NOT_COMPLETED_CONSECUTIVE_RED: "Ikke gennemført planlagte dage",
  NOT_COMPLETED_CONSECUTIVE_YELLOW: "Ikke gennemført planlagte dage",
  UNDER_50_LAST_5_PLANNED_RED: "<50% gennemført de sidste 5 planlagte dage",
  UNDER_50_LAST_5_PLANNED_YELLOW: "<50% gennemført de sidste 5 planlagte dage",
  LATEST_AREA_UNDER_30: "<30% rengjort areal",
  OK: "Ingen advarsler",
};

const CONSECUTIVE_FAIL_REASON_CODES = new Set([
  "NOT_COMPLETED_CONSECUTIVE_RED",
  "NOT_COMPLETED_CONSECUTIVE_YELLOW",
]);

const UNDER_50_REASON_CODES = new Set([
  "UNDER_50_LAST_5_PLANNED_RED",
  "UNDER_50_LAST_5_PLANNED_YELLOW",
]);

const DAY_STATUS_LABEL = {
  complete: "Gennemført",
  complete_with_error: "Gennemført (m/ fejl)",
  failed: "Ikke gennemført",
  pending: "Afventer",
  no_run: "Ingen kørsel",
};

const STATE_SEVERITY_RANK = {
  RED: 3,
  YELLOW: 2,
  GREEN: 1,
};

const mapPins = [
  // G108
  { id: 1, label: "G108-140", x: 68.65, y: 53.34, color: "#23be14" },
  { id: 2, label: "G108-130", x: 67.57, y: 52.05, color: "#23be14" },
  { id: 3, label: "G108-120", x: 65.2, y: 51.93, color: "#23be14" },
  { id: 4, label: "G108-116", x: 63.32, y: 53.53, color: "#23be14" },
  // G109
  { id: 5, label: "G109-101", x: 63.53, y: 50.34, color: "#23be14" },
  { id: 6, label: "G109-110", x: 63.69, y: 47.88, color: "#23be14" },
  // G118
  { id: 7, label: "G118-104", x: 65.42, y: 42.97, color: "#23be14" },
  { id: 8, label: "G118-112", x: 65.26, y: 41.31, color: "#23be14" },
  { id: 9, label: "G118-121", x: 67.03, y: 41.74, color: "#23be14" },
  { id: 10, label: "G118-130", x: 69.24, y: 41.8, color: "#23be14" },
  // G114
  { id: 11, label: "G114-112", x: 71.77, y: 45.55, color: "#23be14" },
  { id: 12, label: "G114-130", x: 71.77, y: 49.35, color: "#23be14" },
  { id: 13, label: "G114-140", x: 71.72, y: 51.13, color: "#23be14" },
  // G193
  { id: 14, label: "G193-110", x: 76.3, y: 43.34, color: "#23be14" },
  { id: 15, label: "G193-120", x: 76.4, y: 44.81, color: "#23be14" },
  { id: 16, label: "G193-130", x: 76.35, y: 46.1, color: "#23be14" },
  { id: 17, label: "G193-140", x: 76.35, y: 47.33, color: "#23be14" },
  { id: 18, label: "G193-150", x: 76.35, y: 48.37, color: "#23be14" },
  { id: 19, label: "G193-160", x: 76.4, y: 49.35, color: "#23be14" },
  // H107
  { id: 20, label: "H107-132", x: 79.85, y: 52.73, color: "#23be14" },
  { id: 21, label: "H107-119", x: 82.06, y: 52.67, color: "#23be14" },
  { id: 22, label: "H107-111", x: 83.4, y: 52.67, color: "#23be14" },
  // G193-170 / G192
  { id: 23, label: "G193-170", x: 76.24, y: 53.1, color: "#23be14" },
  { id: 24, label: "G192-100", x: 76.08, y: 54.33, color: "#23be14" },
  { id: 25, label: "G192-110", x: 75.86, y: 55.25, color: "#23be14" },
  { id: 26, label: "G192-125", x: 75.38, y: 56.29, color: "#23be14" },
  // G191
  { id: 27, label: "G191-170 A/180/190", x: 65.96, y: 58.41, color: "#23be14" },
  { id: 28, label: "G191-150/160/170B", x: 64.12, y: 58.41, color: "#23be14" },
  { id: 29, label: "G191-140", x: 62.02, y: 58.32, color: "#23be14" },
  // G190
  { id: 30, label: "G190-102 D", x: 50.72, y: 56.54, color: "#23be14" },
  { id: 31, label: "G190-104", x: 51.95, y: 56.57, color: "#23be14" },
  { id: 32, label: "G190-110", x: 55.78, y: 58.68, color: "#23be14" },
  { id: 33, label: "G190-120", x: 54.43, y: 58.68, color: "#23be14" },
  // G101
  { id: 34, label: "G101-101", x: 48.62, y: 53.96, color: "#23be14" },
  { id: 35, label: "G101-106", x: 48.67, y: 51.93, color: "#23be14" },
  // G104
  { id: 36, label: "G104-143", x: 53.49, y: 77.49, color: "#23be14" },
  // J105 / J103
  { id: 37, label: "J105-101", x: 72.37, y: 13.14, color: "#23be14" },
  { id: 38, label: "J105-110", x: 73.83, y: 13.2, color: "#23be14" },
  { id: 39, label: "J103-101", x: 76.36, y: 22.65, color: "#23be14" },
  { id: 40, label: "J103-110", x: 78.14, y: 22.47, color: "#23be14" },
  { id: 41, label: "J105-172", x: 75.29, y: 14.8, color: "#23be14" },
  // J601
  { id: 42, label: "J601-1", x: 66.99, y: 34.13, color: "#23be14" },
  { id: 43, label: "J601-2", x: 65.64, y: 34.13, color: "#23be14" },
  // J201
  { id: 44, label: "J201-1", x: 69.31, y: 36.1, color: "#23be14" },
  { id: 45, label: "J201-2", x: 68.23, y: 36.03, color: "#23be14" },
  { id: 46, label: "J201-3", x: 67.1, y: 36.1, color: "#23be14" },
  { id: 47, label: "J201-4", x: 66.02, y: 36.16, color: "#23be14" },
  { id: 48, label: "J201-5", x: 64.84, y: 36.1, color: "#23be14" },
];

const MAP_ASPECT_RATIO = "3000 / 2632";
const ROOM_CLICK_ZOOM = 2.4;
const ENABLE_MAP_CALIBRATION =
  (process.env.REACT_APP_ENABLE_MAP_CALIBRATION || "").toLowerCase() === "true";

const RED_RULES = [
  "Robotter der ikke er nået tilbage i laderen",
  "Robotter der ikke har gennemført sine opgaver 3 dage i træk (omklædning og kontor)",
  "Robotter der ikke har gennemført sine opgaver i 2 dage i træk (klinikstreng)",
  "Robotter der har gennemført mindre end 50% i de sidste 3 dage af 5 planlagte dage (kontor og omklædning)",
  "Robotter der har gennemført mindre end 50% i de sidste 2 dage af 5 planlagte dage (klinik)",
];

const YELLOW_RULES = [
  "Robotter der ikke har gennemført “jobbet” i op til 2 dage (omklædning og kontor)",
  "Robotter der ikke har gennemført “jobbet” i op til 1 dag (klinikstreng)",
  "Robotten har rengjort mindre end 30% af det tilgængelige areal",
  "Robotter der har gennemført mindre end 50% i op til 2 dage af de sidste 5 planlagte dage (kontor og omklædning)",
  "Robotter der har gennemført mindre end 50% i 1 dag af de sidste 5 planlagte dage (klinik)",
];

const SCHEDULE_TYPE_LABEL = {
  reservation: "Reservation",
  doNotDisturb: "Forstyr ikke",
};

// device_cleaning_performance history page size for the List tab's expanded History view.
const PERFORMANCE_HISTORY_PAGE_SIZE = 10;

function parseMaybeJsonArray(value) {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function formatHistoryTimestamp(value) {
  if (!value) return "–";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "–";
  const dateTime = new Intl.DateTimeFormat("da-DK", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(parsed);
  return `${formatWeekdayAbbrev(parsed)} ${dateTime}`;
}

// Capitalized short weekday (e.g. "Man") in the local timezone, matching the
// abbreviation style already used for planned-day rows elsewhere on this page.
function formatWeekdayAbbrev(date) {
  const weekday = new Intl.DateTimeFormat("da-DK", { weekday: "short" })
    .format(date)
    .replace(/\.$/, "");
  return weekday.charAt(0).toUpperCase() + weekday.slice(1);
}

function toLocalDateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatGapDate(dateKey) {
  const date = new Date(`${dateKey}T00:00:00`);
  const dateStr = new Intl.DateTimeFormat("da-DK", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
  return `${formatWeekdayAbbrev(date)} ${dateStr}`;
}

// Lists weekday-only date keys strictly between two date keys (both exclusive),
// descending, so gaps in the performance history can be shown as "no run" rows.
function listWeekdayGapDateKeys(fromDateKeyExclusive, toDateKeyExclusive) {
  const from = new Date(`${fromDateKeyExclusive}T00:00:00`);
  const to = new Date(`${toDateKeyExclusive}T00:00:00`);
  const gaps = [];
  const cursor = new Date(from);
  cursor.setDate(cursor.getDate() - 1);
  while (cursor > to) {
    const weekday = cursor.getDay();
    if (weekday !== 0 && weekday !== 6) {
      gaps.push(toLocalDateKey(cursor));
    }
    cursor.setDate(cursor.getDate() - 1);
  }
  return gaps;
}

// Caps area_cleaned to the room's usable area when that figure is available,
// otherwise just floors it at 0 (mirrors the Daily Status planned-days table).
function computeCappedAreaCleaned(areaCleaned, roomArea) {
  const cleaned = Number(areaCleaned);
  if (!Number.isFinite(cleaned)) {
    return null;
  }
  const cap = Number(roomArea);
  return Number.isFinite(cap)
    ? Math.round(Math.min(Math.max(cleaned, 0), cap))
    : Math.round(Math.max(cleaned, 0));
}

function formatPercentage(value) {
  if (typeof value !== "number" && typeof value !== "string") {
    return null;
  }
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return null;
  }
  return Math.round(Math.min(Math.max(num, 0), 100));
}

// Loads a device's device_cleaning_performance history, 10 rows at a time,
// fetching the next page only once the user scrolls near the bottom.
function DeviceOperationHistoryPanel({ devicePkId, active }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const loadedOnceRef = useRef(false);
  const realRowCountRef = useRef(0);
  // Tracks the date last accounted for, so weekday gaps can be filled between
  // "today" and the newest row, and then between each pair of real rows.
  const cursorDateKeyRef = useRef(toLocalDateKey(new Date()));

  const loadNextPage = useCallback(async () => {
    if (!devicePkId || loading || !hasMore) return;

    setLoading(true);
    setError("");
    try {
      const result = await getCleaningPerformanceRows({
        devicePkId,
        limit: PERFORMANCE_HISTORY_PAGE_SIZE,
        offset: realRowCountRef.current,
      });
      const pageRows = Array.isArray(result?.rows) ? result.rows : [];
      realRowCountRef.current += pageRows.length;

      const additions = [];
      pageRows.forEach((row) => {
        const rowDateKey = toLocalDateKey(new Date(row.cleaning_start_time));
        listWeekdayGapDateKeys(cursorDateKeyRef.current, rowDateKey).forEach(
          (dateKey) => {
            additions.push({ kind: "gap", key: `gap-${dateKey}`, dateKey });
          },
        );
        additions.push({ kind: "data", key: row.pk_id, row });
        cursorDateKeyRef.current = rowDateKey;
      });

      setItems((prev) => [...prev, ...additions]);
      setHasMore(pageRows.length === PERFORMANCE_HISTORY_PAGE_SIZE);
    } catch (err) {
      console.error("Failed to load device performance history:", err);
      setError("Kunne ikke hente historik.");
    } finally {
      setLoading(false);
    }
  }, [devicePkId, hasMore, loading]);

  useEffect(() => {
    if (active && !loadedOnceRef.current) {
      loadedOnceRef.current = true;
      loadNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const handleScroll = (event) => {
    const el = event.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) {
      loadNextPage();
    }
  };

  return (
    <Box>
      <Box onScroll={handleScroll} sx={{ maxHeight: 280, overflowY: "auto" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Dato</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Rengjort areal (m²)</TableCell>
              <TableCell>% rengjort</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => {
              if (item.kind === "gap") {
                return (
                  <TableRow key={item.key}>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {formatGapDate(item.dateKey)}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {DAY_STATUS_LABEL.no_run}
                    </TableCell>
                    <TableCell>–</TableCell>
                    <TableCell>–</TableCell>
                  </TableRow>
                );
              }

              const row = item.row;
              const statusKey =
                Number(row.job_complete_with_error) === 1
                  ? "complete_with_error"
                  : Number(row.job_complete) === 1
                    ? "complete"
                    : "failed";
              const cappedArea = computeCappedAreaCleaned(
                row.area_cleaned,
                row.room_area_minus_inventory_minus_things_on_floor,
              );
              const auhPct = formatPercentage(row.auh_percentage_cleaned);

              return (
                <TableRow key={item.key}>
                  <TableCell>
                    {formatHistoryTimestamp(row.cleaning_start_time)}
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      arrow
                      placement="top"
                      title={row.error_reason || ""}
                      disableHoverListener={!row.error_reason}
                    >
                      <Box
                        component="span"
                        sx={{
                          cursor: row.error_reason ? "help" : "default",
                          textDecoration: row.error_reason
                            ? "underline dotted"
                            : "none",
                        }}
                      >
                        {DAY_STATUS_LABEL[statusKey]}
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{cappedArea ?? "–"}</TableCell>
                  <TableCell sx={{ color: getPercentageColour(auhPct) }}>
                    {auhPct != null ? `${auhPct}%` : "–"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
            <CircularProgress size={18} />
          </Box>
        )}
      </Box>
      {error && (
        <Typography variant="caption" color="error.main">
          {error}
        </Typography>
      )}
      {!loading && items.length === 0 && !error && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 1, textAlign: "center" }}
        >
          Ingen historik fundet.
        </Typography>
      )}
    </Box>
  );
}

// Reads live from device_schedules — small enough per device to load in one call.
function DeviceSchedulesPanel({ devicePkId, active }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const loadedOnceRef = useRef(false);

  useEffect(() => {
    if (!active || loadedOnceRef.current || !devicePkId) return;
    loadedOnceRef.current = true;

    (async () => {
      setLoading(true);
      setError("");
      try {
        const rows = await getDeviceSchedules(devicePkId);
        setSchedules(rows);
      } catch (err) {
        console.error("Failed to load device schedules:", err);
        setError("Kunne ikke hente tidsplaner.");
      } finally {
        setLoading(false);
      }
    })();
  }, [active, devicePkId]);

  return (
    <Box>
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={18} />
        </Box>
      )}
      {error && (
        <Typography variant="caption" color="error.main">
          {error}
        </Typography>
      )}
      {!loading && !error && schedules.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 1, textAlign: "center" }}
        >
          Ingen tidsplaner fundet.
        </Typography>
      )}
      {!loading && schedules.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Aktiv</TableCell>
              <TableCell>Dage</TableCell>
              <TableCell>Tid</TableCell>
              <TableCell>Rengøringstilstand</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {schedules.map((schedule) => (
              <TableRow key={schedule.pk_id}>
                <TableCell>
                  {SCHEDULE_TYPE_LABEL[schedule.schedule_type] ||
                    schedule.schedule_type}
                </TableCell>
                <TableCell>
                  {Number(schedule.enabled) === 1 ? "Ja" : "Nej"}
                </TableCell>
                <TableCell>
                  {parseMaybeJsonArray(schedule.day_of_week).join(", ") || "–"}
                </TableCell>
                <TableCell>
                  {schedule.start_time || "–"}
                  {schedule.end_time ? ` - ${schedule.end_time}` : ""}
                </TableCell>
                <TableCell>{schedule.cleaning_mode || "–"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

// Expanded-row detail view for the List tab: history + schedules, tab-switchable.
function DeviceOperationDetailTabs({ devicePkId }) {
  const [tab, setTab] = useState("history");

  return (
    <Box>
      <Tabs
        value={tab}
        onChange={(event, value) => setTab(value)}
        sx={{ minHeight: 32, mb: 1 }}
      >
        <Tab
          value="history"
          label="History"
          sx={{ minHeight: 32, textTransform: "none" }}
        />
        <Tab
          value="schedules"
          label="Schedules"
          sx={{ minHeight: 32, textTransform: "none" }}
        />
      </Tabs>
      <Box
        sx={{
          display: tab === "history" ? "block" : "none",
          minHeight: 300,
          maxHeight: 300,
        }}
      >
        <DeviceOperationHistoryPanel
          devicePkId={devicePkId}
          active={tab === "history"}
        />
      </Box>
      <Box
        sx={{
          display: tab === "schedules" ? "block" : "none",
          minHeight: 300,
          maxHeight: 300,
        }}
      >
        <DeviceSchedulesPanel
          devicePkId={devicePkId}
          active={tab === "schedules"}
        />
      </Box>
    </Box>
  );
}

function StatusTable({
  title,
  rows,
  onRoomHover,
  onRoomLeave,
  onRoomClick,
  onAssignRow,
  enableAssignment,
  tooltipSeverity,
  headerExtra,
  isAdmin,
  expandedVariant = "ruleDetails",
  isListView = false,
}) {
  const [assignAnchorEl, setAssignAnchorEl] = useState(null);
  const [selectedAssignRow, setSelectedAssignRow] = useState(null);
  const [manualAssignedByRowId, setManualAssignedByRowId] = useState({});
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadUsersError, setLoadUsersError] = useState("");
  const [assignError, setAssignError] = useState("");
  const [assigningRowId, setAssigningRowId] = useState(null);
  const [rcTeknikerNote, setRcTeknikerNote] = useState("");
  const [expandedRowId, setExpandedRowId] = useState(null);

  const loadAssignableUsers = async () => {
    try {
      setLoadingUsers(true);
      setLoadUsersError("");
      const users = await getAllUsers();
      const normalizedUsers = (Array.isArray(users) ? users : [])
        .filter((user) => {
          const role = String(user?.role || "")
            .trim()
            .toLowerCase();
          return role === "technician" || role === "user";
        })
        .map((user) => {
          const username = String(user?.uk_username || "").trim();
          const email = String(user?.uk_email || "").trim();
          const label = username || email || `Bruger ${user?.pk_id || ""}`;
          return {
            id: user?.pk_id,
            role: String(user?.role || "")
              .trim()
              .toLowerCase(),
            customerPkId: user?.customer_pk_id ?? user?.customer_id ?? null,
            label,
            secondary: email && email !== label ? email : "",
          };
        })
        .filter((user) => user.id != null);

      setAvailableUsers(normalizedUsers);
    } catch (error) {
      console.error("Failed to load assignable users:", error);
      setAvailableUsers([]);
      setLoadUsersError("Kunne ikke hente brugere.");
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAssignClick = async (event, row) => {
    event.stopPropagation();
    setAssignAnchorEl(event.currentTarget);
    setSelectedAssignRow(row);
    setAssignError("");
    setRcTeknikerNote("");
    if (availableUsers.length === 0 && !loadingUsers) {
      await loadAssignableUsers();
    }
  };

  const handleAssignClose = () => {
    setAssignAnchorEl(null);
    setSelectedAssignRow(null);
    setAssignError("");
    setRcTeknikerNote("");
  };

  const handleAssigneeSelect = async (assignee, row, options = {}) => {
    if (!row?.id) {
      handleAssignClose();
      return;
    }

    try {
      setAssigningRowId(row.id);
      setAssignError("");

      if (typeof assignee?.id === "number") {
        await onAssignRow?.(row, assignee, {
          note: String(options?.note || "").trim(),
        });
      }

      handleAssignClose();
    } catch (error) {
      console.error("Failed to assign technician to device operation:", error);
      setAssignError(error?.message || "Tildeling mislykkedes.");
    } finally {
      setAssigningRowId(null);
    }
  };

  const openDevicePage = (rcDeviceId) => {
    if (!rcDeviceId) return;
    window.open(`/device/${rcDeviceId}`, "_blank");
  };

  const limitPercentage = (value) => {
    if (typeof value !== "number" || Number.isNaN(value)) {
      return 0;
    }
    return Math.round(Math.min(Math.max(value, 0), 100));
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "#f8fafc",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Tooltip
            arrow
            placement="left-start"
            enterDelay={120}
            title={
              <Box sx={{ maxWidth: 380, py: 0.4 }}>
                {tooltipSeverity !== "yellow" && (
                  <>
                    <Typography
                      sx={{
                        fontSize: "0.74rem",
                        fontWeight: 700,
                        color: "#fca5a5",
                        mb: 0.4,
                      }}
                    >
                      Rød
                    </Typography>
                    <Box
                      component="ul"
                      sx={{
                        m: 0,
                        mb: tooltipSeverity !== "red" ? 1 : 0,
                        pl: 2,
                        display: "grid",
                        gap: 0.35,
                      }}
                    >
                      {RED_RULES.map((item) => (
                        <Typography
                          key={item}
                          component="li"
                          sx={{ fontSize: "0.7rem", lineHeight: 1.35 }}
                        >
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  </>
                )}
                {tooltipSeverity !== "red" && (
                  <>
                    <Typography
                      sx={{
                        fontSize: "0.74rem",
                        fontWeight: 700,
                        color: "#fde68a",
                        mb: 0.4,
                      }}
                    >
                      Gul
                    </Typography>
                    <Box
                      component="ul"
                      sx={{ m: 0, pl: 2, display: "grid", gap: 0.35 }}
                    >
                      {YELLOW_RULES.map((item) => (
                        <Typography
                          key={item}
                          component="li"
                          sx={{ fontSize: "0.7rem", lineHeight: 1.35 }}
                        >
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  </>
                )}
              </Box>
            }
          >
            <InfoOutlinedIcon
              sx={{
                fontSize: 18,
                color: "#4b7282",
                cursor: "help",
                "&:hover": { color: "#0d3342" },
              }}
            />
          </Tooltip>
          {headerExtra ? <Box sx={{ ml: "auto" }}>{headerExtra}</Box> : null}
        </Stack>
      </Box>
      <Table size="small">
        <TableHead>
          {isListView ? (
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Rum</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 40 }} />
            </TableRow>
          ) : (
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Rute</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Rum</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 40 }} />
            </TableRow>
          )}
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} sx={{ py: 2.5, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  alt håndteret
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {rows.map((row) => {
            const assignedUser =
              row.assignedTechnicianName ||
              manualAssignedByRowId[row.id] ||
              null;
            const isActioned = Boolean(assignedUser);
            const assignedAtLabel = formatAssignedAt(row.assignedAt);
            const isExpanded = expandedRowId === row.id;

            return (
              <React.Fragment key={row.id}>
                <TableRow
                  hover
                  title={isActioned ? `Original type: ${row.alert}` : undefined}
                  onMouseEnter={() => onRoomHover(row.room)}
                  onMouseLeave={onRoomLeave}
                  onClick={() => {
                    onRoomClick(row.room);
                    setExpandedRowId(null);
                  }}
                  sx={{
                    cursor: "pointer",
                    ...(isActioned
                      ? {
                          "& > td": {
                            bgcolor: "#dbeafea9",
                            color: "#000000",
                          },
                        }
                      : {}),
                  }}
                >
                  {!isListView && <TableCell>{row.route}</TableCell>}
                  <TableCell>
                    <Box sx={{ display: "grid" }}>
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{ fontWeight: 600 }}
                      >
                        {row.room}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "0.7rem",
                          lineHeight: 1.3,
                          color:
                            getRoomRestrictionColorFromLabel(row.roomType) ||
                            "text.secondary",
                          fontWeight: getRoomRestrictionColorFromLabel(
                            row.roomType,
                          )
                            ? 700
                            : 400,
                        }}
                      >
                        {stripRoomRestrictionSuffix(row.roomType)}
                      </Typography>
                    </Box>
                  </TableCell>
                  {!isListView && (
                    <TableCell
                      sx={{
                        bgcolor: isActioned
                          ? "#dbeafea9"
                          : ALERT_STYLES[row.severity]?.typeBg,
                        color: isActioned
                          ? "#000000"
                          : ALERT_STYLES[row.severity]?.typeText,
                        fontWeight: 600,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <Box sx={{ display: "grid", gap: 0.25 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {row.alert}
                          </Typography>
                          {isActioned && (assignedUser || assignedAtLabel) && (
                            <Typography
                              variant="caption"
                              sx={{ color: "#000000", fontWeight: 500 }}
                            >
                              {assignedUser
                                ? `Tildelt til: ${assignedUser}`
                                : ""}
                              {assignedUser && assignedAtLabel ? " · " : ""}
                              {assignedAtLabel
                                ? `Tildelt: ${assignedAtLabel}`
                                : ""}
                            </Typography>
                          )}
                        </Box>
                        {enableAssignment && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(event) => handleAssignClick(event, row)}
                            disabled={assigningRowId === row.id}
                            sx={{
                              minWidth: 0,
                              textTransform: "none",
                              bgcolor: "#fff",
                            }}
                          >
                            {assigningRowId === row.id
                              ? "tildeler..."
                              : assignedUser || "tildel"}
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  )}

                  {isListView && (
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <Box sx={{ display: "grid", gap: 0.25 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {row.alert}
                          </Typography>
                          {isActioned && (assignedUser || assignedAtLabel) && (
                            <Typography
                              variant="caption"
                              sx={{ color: "#000000", fontWeight: 500 }}
                            >
                              {assignedUser
                                ? `Tildelt til: ${assignedUser}`
                                : ""}
                              {assignedUser && assignedAtLabel ? " · " : ""}
                              {assignedAtLabel
                                ? `Tildelt: ${assignedAtLabel}`
                                : ""}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                  )}

                  <TableCell sx={{ px: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        setExpandedRowId((prev) =>
                          prev === row.id ? null : row.id,
                        );
                      }}
                    >
                      {isExpanded ? (
                        <KeyboardArrowUpIcon fontSize="small" />
                      ) : (
                        <KeyboardArrowDownIcon fontSize="small" />
                      )}
                    </IconButton>
                  </TableCell>
                </TableRow>
                {isExpanded && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ bgcolor: "#f8fafc", py: 2 }}>
                      {expandedVariant === "deviceTabs" ? (
                        <DeviceOperationDetailTabs
                          devicePkId={row.devicePkId}
                        />
                      ) : row.ruleDetails ? (
                        <Stack spacing={1.25}>
                          <Stack
                            direction="row"
                            spacing={3}
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <Typography variant="body2">
                              <strong>Sammenhængende fejlede dage:</strong>{" "}
                              {row.consecutiveFailDays ?? "–"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Dage &lt;50%:</strong>{" "}
                              {row.lowUnder50Days ?? "–"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Retur til lader:</strong>{" "}
                              {Number(row.hasReturnedToCharger) === 1
                                ? "Ja"
                                : "Nej"}
                            </Typography>
                            {isAdmin && (
                              <Tooltip
                                arrow
                                placement="top"
                                title="Åbn enhedsside"
                              >
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => openDevicePage(row.rcDeviceId)}
                                >
                                  <OpenInNewOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                          {Array.isArray(row.ruleDetails.planned_days_detail) &&
                            row.ruleDetails.planned_days_detail.length > 0 && (
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600, mb: 0.5 }}
                                >
                                  Planlagte dage:
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Dato</TableCell>
                                      <TableCell>Status</TableCell>
                                      <TableCell>Rengjort areal (m²)</TableCell>
                                      <TableCell>% rengjort</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {row.ruleDetails.planned_days_detail.map(
                                      (entry) => (
                                        <TableRow key={entry.day}>
                                          <TableCell>
                                            {formatPlannedDay(entry.day)}
                                          </TableCell>
                                          <TableCell>
                                            <Tooltip
                                              arrow
                                              placement="top"
                                              title={entry.error_reason || ""}
                                              disableHoverListener={
                                                !entry.error_reason
                                              }
                                            >
                                              <Box
                                                component="span"
                                                sx={{
                                                  cursor: entry.error_reason
                                                    ? "help"
                                                    : "default",
                                                  textDecoration:
                                                    entry.error_reason
                                                      ? "underline dotted"
                                                      : "none",
                                                }}
                                              >
                                                {DAY_STATUS_LABEL[
                                                  entry.status
                                                ] || entry.status}
                                              </Box>
                                            </Tooltip>
                                          </TableCell>
                                          <TableCell>
                                            {Number.isFinite(
                                              Number(entry.area_cleaned),
                                            )
                                              ? Math.round(
                                                  Number.isFinite(
                                                    Number(
                                                      entry.room_area_minus_inventory_minus_things_on_floor,
                                                    ),
                                                  )
                                                    ? Math.min(
                                                        Math.max(
                                                          Number(
                                                            entry.area_cleaned,
                                                          ),
                                                          0,
                                                        ),
                                                        Number(
                                                          entry.room_area_minus_inventory_minus_things_on_floor,
                                                        ),
                                                      )
                                                    : Math.max(
                                                        Number(
                                                          entry.area_cleaned,
                                                        ),
                                                        0,
                                                      ),
                                                )
                                              : "–"}
                                          </TableCell>
                                          <TableCell
                                            sx={{
                                              color: getPercentageColour(
                                                entry.auh_percentage_cleaned,
                                              ),
                                            }}
                                          >
                                            {entry.auh_percentage_cleaned !=
                                            null
                                              ? `${limitPercentage(entry.auh_percentage_cleaned)}%`
                                              : "–"}
                                          </TableCell>
                                        </TableRow>
                                      ),
                                    )}
                                  </TableBody>
                                </Table>
                              </Box>
                            )}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Ingen regeldetaljer tilgængelige.
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
      <Popover
        open={Boolean(assignAnchorEl)}
        anchorEl={assignAnchorEl}
        onClose={handleAssignClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Box sx={{ p: 1, display: "grid", gap: 0.75, minWidth: 190 }}>
          {assignError && (
            <Typography variant="body2" color="error.main">
              {assignError}
            </Typography>
          )}
          {loadingUsers && (
            <Typography variant="body2" color="text.secondary">
              Henter brugere...
            </Typography>
          )}
          {!loadingUsers && loadUsersError && (
            <>
              <Typography variant="body2" color="error.main">
                {loadUsersError}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={loadAssignableUsers}
                sx={{ textTransform: "none" }}
              >
                Prøv igen
              </Button>
            </>
          )}
          {!loadingUsers && !loadUsersError && availableUsers.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Ingen brugere fundet.
            </Typography>
          )}
          {!loadingUsers &&
            !loadUsersError &&
            availableUsers.map((user) => {
              const selectedCustomerId = selectedAssignRow?.customerId;
              const isUserForSelectedCustomer =
                user.role === "user" &&
                String(user.customerPkId || "") ===
                  String(selectedCustomerId || "");

              if (user.role === "user" && !isUserForSelectedCustomer) {
                return null;
              }

              const isRcAccount = isUserForSelectedCustomer;

              if (!isRcAccount) {
                return (
                  <Button
                    key={user.id}
                    variant="outlined"
                    size="small"
                    fullWidth
                    onClick={() =>
                      handleAssigneeSelect(user, selectedAssignRow)
                    }
                    disabled={assigningRowId === selectedAssignRow?.id}
                    sx={{ justifyContent: "flex-start", textTransform: "none" }}
                  >
                    <Box sx={{ display: "grid", textAlign: "left" }}>
                      <Typography variant="body2">{user.label}</Typography>
                      {user.secondary && (
                        <Typography variant="caption" color="text.secondary">
                          {user.secondary}
                        </Typography>
                      )}
                    </Box>
                  </Button>
                );
              }

              return (
                <Box
                  key={user.id}
                  sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                >
                  <TextField
                    size="small"
                    value={rcTeknikerNote}
                    placeholder="Valgfri besked"
                    onChange={(event) => setRcTeknikerNote(event.target.value)}
                    disabled={assigningRowId === selectedAssignRow?.id}
                    sx={{ minWidth: 180 }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() =>
                      handleAssigneeSelect(user, selectedAssignRow, {
                        note: rcTeknikerNote,
                      })
                    }
                    disabled={assigningRowId === selectedAssignRow?.id}
                    sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                  >
                    {user.label}
                  </Button>
                </Box>
              );
            })}
        </Box>
      </Popover>
    </Paper>
  );
}

function toAlertLabel(row) {
  const code = String(row?.primary_reason_code || "").trim();
  if (CONSECUTIVE_FAIL_REASON_CODES.has(code)) {
    const days = Number(row?.consecutive_fail_days);
    if (Number.isFinite(days) && days > 0) {
      return `Ikke gennemført ${days} planlagte dage`;
    }
  }
  if (UNDER_50_REASON_CODES.has(code)) {
    const days = Number(row?.low_under_50_days_last_5_planned);
    if (Number.isFinite(days) && days > 0) {
      return `Gennemført mindre end 50% i ${days} dag${days === 1 ? "" : "e"} af de sidste 5 planlagte dage`;
    }
  }
  return REASON_LABEL_BY_CODE[code] || code || "Ukendt årsag";
}

function parseJsonMaybeString(value) {
  if (!value) {
    return null;
  }
  if (typeof value === "object") {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function formatAssignedAt(value) {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat("da-DK", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(parsed);
}

// Renders a "YYYY-MM-DD" day key as e.g. "Ons 19." for readability.
function formatPlannedDay(dayKey) {
  if (!dayKey) {
    return dayKey;
  }

  const parsed = new Date(`${dayKey}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) {
    return dayKey;
  }

  const weekday = new Intl.DateTimeFormat("da-DK", {
    timeZone: "UTC",
    weekday: "short",
  })
    .format(parsed)
    .replace(/\.$/, "");
  const capitalizedWeekday = weekday.charAt(0).toUpperCase() + weekday.slice(1);

  return `${capitalizedWeekday} ${parsed.getUTCDate()}. Aften`;
}

function isPersistentlyActionedRow(row) {
  return Boolean(row?.assignedTechnicianName || row?.linkedJobPkId);
}

function chooseMoreSevereRow(previousRow, nextRow) {
  if (!previousRow) {
    return nextRow;
  }

  const previousRank = STATE_SEVERITY_RANK[previousRow?.state] || 0;
  const nextRank = STATE_SEVERITY_RANK[nextRow?.state] || 0;

  if (nextRank !== previousRank) {
    return nextRank > previousRank ? nextRow : previousRow;
  }

  const previousTime = new Date(previousRow?.calculated_at || 0).getTime();
  const nextTime = new Date(nextRow?.calculated_at || 0).getTime();
  if (Number.isFinite(previousTime) && Number.isFinite(nextTime)) {
    if (nextTime !== previousTime) {
      return nextTime > previousTime ? nextRow : previousRow;
    }
  }

  return Number(nextRow?.pk_id || 0) > Number(previousRow?.pk_id || 0)
    ? nextRow
    : previousRow;
}

function toStatusRow(row) {
  return {
    id: row.pk_id,
    devicePkId: row.device_pk_id || null,
    rcDeviceId: row.rc_device_id || null,
    customerId: row.customer_id || null,
    route: row.route_name || "Ingen rute",
    room: row.room_name || `Rum ${row.room_pk_id || "ukendt"}`,
    roomType: row.room_type_name || "Ukendt",
    alert: toAlertLabel(row),
    severity: String(row.state || "").toLowerCase(),
    manufacturer: row.manufacturer || "Ukendt producent",
    assignedTechnicianName: row.linked_job_assigned_username || null,
    linkedJobPkId: row.created_job_pk_id || null,
    linkedJobStatus: row.linked_job_status || null,
    assignedAt: row.linked_job_assigned_at || null,
    consecutiveFailDays: row.consecutive_fail_days ?? null,
    lowUnder50Days: row.low_under_50_days_last_5_planned ?? null,
    latestAreaPct: row.latest_area_pct ?? null,
    hasReturnedToCharger: row.has_returned_to_charger,
    ruleDetails: parseJsonMaybeString(row.rule_details),
  };
}

export default function DriftsdashboardPage() {
  const mapViewportRef = useRef(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const dragStateRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    startPanX: 0,
    startPanY: 0,
  });
  const dragMovedRef = useRef(false);
  const centerTooltipTimeoutRef = useRef(null);

  const [zoom, setZoom] = useState(1.8);
  const [pan, setPan] = useState({ x: -154.4, y: 95 });
  const [isDragging, setIsDragging] = useState(false);
  const [hoveredPinId, setHoveredPinId] = useState(null);
  const [isCalibrating, setIsCalibrating] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(mapPins[0]?.label || "");
  const [selectedFloor, setSelectedFloor] = useState(FLOOR_LEVELS[0].id);
  const [pinOverrides, setPinOverrides] = useState({});
  const [copied, setCopied] = useState(false);
  const [operationsRows, setOperationsRows] = useState([]);
  const [loadingOperations, setLoadingOperations] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [activeStatusTab, setActiveStatusTab] = useState("daily");
  const [listRoomSearch, setListRoomSearch] = useState("");
  const calibrationActive = ENABLE_MAP_CALIBRATION && isCalibrating;

  const isMountedRef = useRef(true);

  const loadDeviceOperations = useCallback(async () => {
    try {
      setLoadError("");
      const rows = await getLatestDeviceOperations({
        limit: 1000,
        offset: 0,
      });
      if (isMountedRef.current) {
        setOperationsRows(Array.isArray(rows) ? rows : []);
      }
      console.table(rows);
    } catch (error) {
      if (isMountedRef.current) {
        console.error("Failed to load latest device operations:", error);
        setOperationsRows([]);
        setLoadError("Kunne ikke hente driftsdata fra API.");
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingOperations(false);
      }
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    setLoadingOperations(true);
    loadDeviceOperations();

    return () => {
      isMountedRef.current = false;
      window.clearTimeout(centerTooltipTimeoutRef.current);
    };
  }, [loadDeviceOperations]);

  // Automation service broadcasts this after writing new device_operations
  // rows, so refresh instead of waiting for the next manual reload.
  const { isConnected: isSSEConnected } = useSSESubscription(
    "driftsdashboard-device-operations",
    useCallback(
      (message) => {
        if (message?.type === "device_operations_updated") {
          loadDeviceOperations();
        }
      },
      [loadDeviceOperations],
    ),
  );

  // Used only to pick which single colour/tooltip a room's map pin shows when
  // multiple devices (e.g. duplicate-named robots) share the same room.
  const operationsByRoom = useMemo(() => {
    const byRoom = {};
    operationsRows.forEach((row) => {
      const roomLabel = String(row?.room_name || "").trim();
      if (!roomLabel) {
        return;
      }
      byRoom[roomLabel] = chooseMoreSevereRow(byRoom[roomLabel], row);
    });
    return byRoom;
  }, [operationsRows]);

  // One row per device so devices sharing a room (e.g. duplicate names)
  // aren't collapsed away in the status/list tables.
  const statusRows = useMemo(
    () => operationsRows.map(toStatusRow),
    [operationsRows],
  );

  const actionedRows = useMemo(
    () => statusRows.filter((row) => isPersistentlyActionedRow(row)),
    [statusRows],
  );

  const actionableRows = useMemo(
    () =>
      statusRows.filter(
        (row) => row.severity === "red" && !isPersistentlyActionedRow(row),
      ),
    [statusRows],
  );

  const infoRows = useMemo(() => {
    const sortedActioned = [...actionedRows].sort((a, b) => {
      const aMs = new Date(a.assignedAt || 0).getTime();
      const bMs = new Date(b.assignedAt || 0).getTime();

      if (Number.isFinite(aMs) && Number.isFinite(bMs) && aMs !== bMs) {
        return bMs - aMs;
      }

      return String(a.room || "").localeCompare(String(b.room || ""));
    });

    const yellowRows = statusRows.filter(
      (row) => row.severity === "yellow" && !isPersistentlyActionedRow(row),
    );

    return [...sortedActioned, ...yellowRows];
  }, [actionedRows, statusRows]);

  const actionableRoomSet = useMemo(
    () => new Set(actionableRows.map((row) => row.room)),
    [actionableRows],
  );

  const infoRoomSet = useMemo(
    () => new Set(infoRows.map((row) => row.room)),
    [infoRows],
  );

  const actionedInfoRoomSet = useMemo(
    () =>
      new Set(
        infoRows
          .filter((row) => isPersistentlyActionedRow(row))
          .map((row) => row.room),
      ),
    [infoRows],
  );

  // "List" tab shows every device operation regardless of severity, sorted
  // alphabetically by room name.
  const listRows = useMemo(() => {
    const term = listRoomSearch.trim().toLowerCase();
    const filtered = term
      ? statusRows.filter((row) =>
          String(row.room || "")
            .toLowerCase()
            .includes(term),
        )
      : statusRows;

    return [...filtered].sort((a, b) =>
      String(a.room || "").localeCompare(String(b.room || "")),
    );
  }, [listRoomSearch, statusRows]);

  const clampZoom = (value) => Math.min(3, Math.max(1, value));

  const clampPan = (x, y, targetZoom = zoom) => {
    const viewport = mapViewportRef.current;
    if (!viewport || targetZoom <= 1) {
      return { x: 0, y: 0 };
    }

    const maxX = ((targetZoom - 1) * viewport.clientWidth) / 2;
    const maxY = ((targetZoom - 1) * viewport.clientHeight) / 2;

    return {
      x: Math.min(maxX, Math.max(-maxX, x)),
      y: Math.min(maxY, Math.max(-maxY, y)),
    };
  };

  const adjustZoom = (delta) => {
    setZoom((prev) => {
      const next = clampZoom(prev + delta);
      setPan((prevPan) => clampPan(prevPan.x, prevPan.y, next));
      return next;
    });
  };

  const zoomIn = () => adjustZoom(0.2);
  const zoomOut = () => adjustZoom(-0.2);
  const resetZoom = () => {
    setZoom(1.8);
    setPan({ x: -154.4, y: 95 });
  };

  const startDrag = (clientX, clientY) => {
    if (zoom <= 1) return;
    dragMovedRef.current = false;
    dragStateRef.current = {
      active: true,
      startX: clientX,
      startY: clientY,
      startPanX: pan.x,
      startPanY: pan.y,
    };
    setIsDragging(true);
  };

  const moveDrag = (clientX, clientY) => {
    if (!dragStateRef.current.active) return;
    const dx = clientX - dragStateRef.current.startX;
    const dy = clientY - dragStateRef.current.startY;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
      dragMovedRef.current = true;
    }
    const nextX = dragStateRef.current.startPanX + dx;
    const nextY = dragStateRef.current.startPanY + dy;
    setPan(clampPan(nextX, nextY));
  };

  const endDrag = () => {
    dragStateRef.current.active = false;
    setIsDragging(false);
  };

  const handleMapWheel = (event) => {
    event.preventDefault();
    const delta = event.deltaY > 0 ? -0.1 : 0.1;
    adjustZoom(delta);
  };

  const getMapPercentFromClientPoint = (clientX, clientY) => {
    const viewport = mapViewportRef.current;
    if (!viewport) return null;

    const rect = viewport.getBoundingClientRect();
    const viewportX = clientX - rect.left;
    const viewportY = clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const imageX = centerX + (viewportX - centerX - pan.x) / zoom;
    const imageY = centerY + (viewportY - centerY - pan.y) / zoom;

    const x = Math.min(100, Math.max(0, (imageX / rect.width) * 100));
    const y = Math.min(100, Math.max(0, (imageY / rect.height) * 100));

    return {
      x: Number(x.toFixed(2)),
      y: Number(y.toFixed(2)),
    };
  };

  const handleCalibrateClick = (event) => {
    if (!calibrationActive || !selectedRoom) return;
    if (dragMovedRef.current) {
      dragMovedRef.current = false;
      return;
    }

    const nextPoint = getMapPercentFromClientPoint(
      event.clientX,
      event.clientY,
    );
    if (!nextPoint) return;

    setPinOverrides((prev) => ({
      ...prev,
      [selectedRoom]: nextPoint,
    }));
  };

  const mergedPins = mapPins.map((pin) => {
    const override = pinOverrides[pin.label];
    const operation = operationsByRoom[pin.label] || null;
    const severityColor = actionedInfoRoomSet.has(pin.label)
      ? ALERT_STYLES.actioned.pin
      : infoRoomSet.has(pin.label)
        ? ALERT_STYLES.yellow.pin
        : actionableRoomSet.has(pin.label)
          ? ALERT_STYLES.red.pin
          : ALERT_STYLES[String(operation?.state || "").toLowerCase()]?.pin;

    if (override) {
      return {
        ...pin,
        ...override,
        route: operation?.route_name || null,
        manufacturer: operation?.manufacturer || null,
        color: severityColor || pin.color,
      };
    }

    return {
      ...pin,
      route: operation?.route_name || null,
      manufacturer: operation?.manufacturer || null,
      color: severityColor || pin.color,
    };
  });

  const pinIdByRoom = mergedPins.reduce((acc, pin) => {
    acc[pin.label] = pin.id;
    return acc;
  }, {});

  const pinLegend = PIN_LEGEND_ITEMS.map((item) => ({
    ...item,
    count: mergedPins.filter((pin) => pin.color === item.color).length,
  }));

  const handleTableRoomHover = (roomLabel) => {
    setHoveredPinId(pinIdByRoom[roomLabel] || null);
  };

  const handleTableRoomLeave = () => {
    setHoveredPinId(null);
  };

  const centerOnPin = (pin, targetZoom = zoom) => {
    const viewport = mapViewportRef.current;
    if (!pin || !viewport) return;

    const rect = viewport.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const imageX = (pin.x / 100) * rect.width;
    const imageY = (pin.y / 100) * rect.height;

    setZoom(targetZoom);
    setPan(
      clampPan(
        (centerX - imageX) * targetZoom,
        (centerY - imageY) * targetZoom,
        targetZoom,
      ),
    );
  };

  const handleTableRoomClick = (roomLabel) => {
    const pin = mergedPins.find((p) => p.label === roomLabel);
    if (!pin) return;

    // Close any open tooltip and wait for the pan transition to finish before
    // reopening it, otherwise the tooltip anchors to the pin's pre-pan
    // position and doesn't follow it while the map animates to center.
    setHoveredPinId(null);
    centerOnPin(pin, clampZoom(Math.max(zoom, ROOM_CLICK_ZOOM)));
    window.clearTimeout(centerTooltipTimeoutRef.current);
    centerTooltipTimeoutRef.current = window.setTimeout(() => {
      setHoveredPinId(pin.id);
    }, 200);
  };

  // Clicking a pin jumps to the List tab with the room already entered in the search field.
  const handlePinClick = (pin) => {
    if (calibrationActive || dragMovedRef.current) return;
    setListRoomSearch(pin.label);
    setActiveStatusTab("list");
  };

  const calibrationPayload = mergedPins.map(
    ({ id, label, x, y, color, route, manufacturer }) => ({
      id,
      label,
      x,
      y,
      color,
      route,
      manufacturer,
    }),
  );

  const copyCalibratedPins = async () => {
    try {
      await navigator.clipboard.writeText(
        JSON.stringify(calibrationPayload, null, 2),
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      setCopied(false);
    }
  };

  const clearSelectedCalibration = () => {
    setPinOverrides((prev) => {
      const next = { ...prev };
      delete next[selectedRoom];
      return next;
    });
  };

  const handleAssignOperation = async (row, assignee, options = {}) => {
    if (typeof assignee?.id !== "number") {
      throw new Error("Invalid assignee");
    }

    const result = await assignDeviceOperation(row.id, assignee.id);
    const updatedOperation = result?.operation;

    if (!updatedOperation) {
      throw new Error(
        "Assignment completed but no updated operation was returned",
      );
    }

    setOperationsRows((prev) =>
      prev.map((operationRow) =>
        Number(operationRow.pk_id) === Number(updatedOperation.pk_id)
          ? { ...operationRow, ...updatedOperation }
          : operationRow,
      ),
    );

    const note = String(options?.note || "").trim();
    const isRcAccount = String(assignee?.role || "").toLowerCase() === "user";

    if (isRcAccount && note) {
      await notifyRcTekniker(row.id, {
        note,
        route_name: row.route || null,
        room_name: row.room || null,
        room_type_name: row.roomType || null,
        alert_label: row.alert || null,
        manufacturer: row.manufacturer || null,
      });
    }
  };

  return (
    <>
      <TopNavBar heading="Driftsdashboard" />
      <Container maxWidth="xl" sx={{ mt: 3, pb: 4 }}>
        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  AUH Kort
                </Typography>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="floor-level-label">Etage</InputLabel>
                  <Select
                    labelId="floor-level-label"
                    value={selectedFloor}
                    label="Etage"
                    onChange={(event) => setSelectedFloor(event.target.value)}
                    disabled={FLOOR_LEVELS.length <= 1}
                  >
                    {FLOOR_LEVELS.map((floor) => (
                      <MenuItem key={floor.id} value={floor.id}>
                        {floor.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Stack direction="row" spacing={0.9} alignItems="center">
                    {pinLegend.map((item) => (
                      <Stack
                        key={item.label}
                        direction="row"
                        spacing={0.35}
                        alignItems="center"
                        title={item.label}
                      >
                        <Box
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            bgcolor: item.color,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{ fontSize: "0.68rem", color: "text.secondary" }}
                        >
                          {item.count}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                  <Box sx={{ display: "flex" }}>
                    <Tooltip title="Zoom out">
                      <IconButton
                        size="small"
                        onClick={zoomOut}
                        disabled={zoom <= 1}
                      >
                        <ZoomOutIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reset zoom">
                      <IconButton
                        size="small"
                        onClick={resetZoom}
                        disabled={zoom === 1}
                      >
                        <RestartAltIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Zoom in">
                      <IconButton
                        size="small"
                        onClick={zoomIn}
                        disabled={zoom >= 3}
                      >
                        <ZoomInIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
              {ENABLE_MAP_CALIBRATION && (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  sx={{ mb: 1.25 }}
                >
                  <Button
                    variant={isCalibrating ? "contained" : "outlined"}
                    onClick={() => setIsCalibrating((prev) => !prev)}
                    size="small"
                  >
                    {isCalibrating ? "Calibration On" : "Calibration Off"}
                  </Button>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="room-select-label">Room</InputLabel>
                    <Select
                      labelId="room-select-label"
                      value={selectedRoom}
                      label="Room"
                      onChange={(event) => setSelectedRoom(event.target.value)}
                      disabled={!isCalibrating}
                    >
                      {mapPins.map((pin) => (
                        <MenuItem key={pin.label} value={pin.label}>
                          {pin.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="small"
                    onClick={clearSelectedCalibration}
                    disabled={!isCalibrating || !pinOverrides[selectedRoom]}
                  >
                    Clear Selected
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={copyCalibratedPins}
                  >
                    {copied ? "Copied" : "Copy Pins JSON"}
                  </Button>
                </Stack>
              )}
              <Box
                ref={mapViewportRef}
                onWheel={handleMapWheel}
                onClick={
                  ENABLE_MAP_CALIBRATION ? handleCalibrateClick : undefined
                }
                onMouseDown={(event) => startDrag(event.clientX, event.clientY)}
                onMouseMove={(event) => moveDrag(event.clientX, event.clientY)}
                onMouseUp={endDrag}
                onMouseLeave={endDrag}
                onTouchStart={(event) => {
                  const touch = event.touches[0];
                  if (touch) startDrag(touch.clientX, touch.clientY);
                }}
                onTouchMove={(event) => {
                  const touch = event.touches[0];
                  if (touch) moveDrag(touch.clientX, touch.clientY);
                }}
                onTouchEnd={endDrag}
                sx={{
                  position: "relative",
                  width: "100%",
                  aspectRatio: MAP_ASPECT_RATIO,
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "#f3f4f6",
                  cursor:
                    zoom > 1 ? (isDragging ? "grabbing" : "grab") : "zoom-in",
                  userSelect: "none",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    willChange: "transform",
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                    transformOrigin: "center center",
                    transition: isDragging
                      ? "none"
                      : "transform 180ms ease-out",
                    backgroundImage: `url(${auhMap})`,
                    backgroundSize: "contain",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      backgroundImage:
                        "linear-gradient(to right, rgba(15,23,42,0.16) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,0.16) 1px, transparent 1px)",
                      backgroundSize: "36px 36px",
                      pointerEvents: "none",
                    }}
                  />

                  {mergedPins.map((pin) => {
                    const isHovered = hoveredPinId === pin.id;
                    const isSelectedForCalibration =
                      calibrationActive && selectedRoom === pin.label;

                    return (
                      <Tooltip
                        key={pin.id}
                        title={
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 700 }}
                            >
                              {pin.label}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ display: "block" }}
                            >
                              {pin.route || "Ingen rute"}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ display: "block" }}
                            >
                              {pin.manufacturer || "Ukendt producent"}
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="top"
                        open={isHovered}
                      >
                        <Box
                          tabIndex={0}
                          onMouseEnter={() => setHoveredPinId(pin.id)}
                          onMouseLeave={() => setHoveredPinId(null)}
                          onFocus={() => setHoveredPinId(pin.id)}
                          onBlur={() => setHoveredPinId(null)}
                          onClick={(event) => {
                            event.stopPropagation();
                            handlePinClick(pin);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              handlePinClick(pin);
                            }
                          }}
                          sx={{
                            position: "absolute",
                            left: `${pin.x}%`,
                            top: `${pin.y}%`,
                            transform: "translate(-50%, -50%)",
                            width: isHovered ? 11 : 8,
                            height: isHovered ? 11 : 8,
                            borderRadius: "50%",
                            bgcolor: pin.color,
                            border: isSelectedForCalibration
                              ? "2px solid #0ea5e9"
                              : "1px solid #fff",
                            boxShadow: isHovered
                              ? "0 4px 14px rgba(0,0,0,0.35)"
                              : "0 2px 10px rgba(0,0,0,0.25)",
                            transition:
                              "width 120ms ease, height 120ms ease, box-shadow 120ms ease",
                            cursor: "pointer",
                            outline: "none",
                          }}
                        />
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <Box sx={{ display: "grid", gap: 1.5 }}>
              <Tabs
                value={activeStatusTab}
                onChange={(event, value) => setActiveStatusTab(value)}
                sx={{
                  minHeight: 36,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Tab
                  value="daily"
                  label="Daglig Status"
                  sx={{ minHeight: 36, textTransform: "none" }}
                />
                <Tab
                  value="list"
                  label="Liste"
                  sx={{ minHeight: 36, textTransform: "none" }}
                />
              </Tabs>
              {loadingOperations && (
                <Typography variant="body2" color="text.secondary">
                  Henter driftsdata...
                </Typography>
              )}
              {loadError && (
                <Typography variant="body2" color="error.main">
                  {loadError}
                </Typography>
              )}
              {activeStatusTab === "daily" && (
                <Box sx={{ display: "grid", gap: 2.5 }}>
                  <StatusTable
                    title="Kræver handling"
                    rows={actionableRows}
                    onRoomHover={handleTableRoomHover}
                    onRoomLeave={handleTableRoomLeave}
                    onRoomClick={handleTableRoomClick}
                    onAssignRow={handleAssignOperation}
                    isAdmin={isAdmin}
                    enableAssignment
                    tooltipSeverity="red"
                  />
                  <StatusTable
                    title="Til information"
                    rows={infoRows}
                    onRoomHover={handleTableRoomHover}
                    onRoomLeave={handleTableRoomLeave}
                    onRoomClick={handleTableRoomClick}
                    onAssignRow={handleAssignOperation}
                    isAdmin={isAdmin}
                    enableAssignment={false}
                    tooltipSeverity="yellow"
                  />
                </Box>
              )}
              {activeStatusTab === "list" && (
                <StatusTable
                  title="Alle enheder"
                  rows={listRows}
                  onRoomHover={handleTableRoomHover}
                  onRoomLeave={handleTableRoomLeave}
                  onRoomClick={handleTableRoomClick}
                  onAssignRow={handleAssignOperation}
                  enableAssignment={false}
                  isListView={true}
                  expandedVariant="deviceTabs"
                  headerExtra={
                    <TextField
                      size="small"
                      placeholder="Søg efter rum..."
                      value={listRoomSearch}
                      onChange={(event) =>
                        setListRoomSearch(event.target.value)
                      }
                      sx={{ maxWidth: 200 }}
                      InputProps={{
                        endAdornment:
                          listRoomSearch.length > 0 ? (
                            <Tooltip title="Clear search">
                              <IconButton
                                size="small"
                                onClick={() => setListRoomSearch("")}
                                sx={{ mr: -0.5 }}
                              >
                                <ClearOutlinedIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          ) : null,
                      }}
                    />
                  }
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>
      <Box
        title={isSSEConnected ? "SSE connected" : "SSE disconnected"}
        sx={{
          position: "fixed",
          bottom: 10,
          right: 15,
          width: 5,
          height: 5,
          borderRadius: "50%",
          bgcolor: isSSEConnected ? "#2563eb" : "#cbd5e1",
          boxShadow: isSSEConnected ? "0 0 4px rgba(37, 99, 235, 0.9)" : "none",
          pointerEvents: "none",
          zIndex: 1300,
        }}
      />
    </>
  );
}
